import React, { useState, useRef, useEffect } from "react";
import Image from "next/image";
import { t } from "@lingui/core/macro";
import { Box, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

interface Country {
  code: string;
  name: string;
  flag: string;
}

interface PhoneInputFieldProps {
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  id?: string;
  name: string;
  placeholder: string;
  value: string;
  required?: boolean;
  setCountryCode: React.Dispatch<React.SetStateAction<string>>;
  countryList: Country[];
  selectedCountry: Country | undefined;
  label?: string;
  onBlur: () => void;
  style?: React.CSSProperties;
}

const PhoneInputField: React.FC<PhoneInputFieldProps> = ({
  value,
  onChange,
  setCountryCode,
  countryList,
  selectedCountry,
  placeholder,
  label,
  style,
  onBlur,
  ...props
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const handleDropdownToggle = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {label && (
        <Box
          component="label"
          htmlFor="phone"
          sx={{
            fontSize: "13px",
            marginBottom: "8px",
            color: "#000",
          }}
        >
          {label}{" "}
          {props.required && (
            <Box component="span" sx={{ color: "#fd7b7c" }}>
              *
            </Box>
          )}
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          position: "relative",
          ...style,
        }}
        ref={dropdownRef}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "rgba(0,0,0,0.05)",
            borderRadius: "4px 0 0 4px",
            padding: "0 10px",
            height: "50px",
            cursor: "pointer",
          }}
          onClick={handleDropdownToggle}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {selectedCountry && (
              <Image
                src={selectedCountry.flag}
                alt={t`Flag of ${selectedCountry.code}`}
                width={25}
                height={25}
                style={{ marginRight: "8px" }}
              />
            )}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <span className="text-base">+{selectedCountry?.code}</span>
              {isDropdownOpen ? (
                <KeyboardArrowUpIcon sx={{ fill: "gray" }} />
              ) : (
                <KeyboardArrowDownIcon sx={{ fill: "gray" }} />
              )}
            </Box>
          </Box>
        </Box>
        <Box
          component="input"
          id="phone"
          name="phone"
          type="tel"
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          onInput={(e) => {
            const input = e.target as HTMLInputElement;
            input.value = input.value.replace(/\D/g, "");
          }}
          onBlur={onBlur}
          sx={{
            flex: 1,
            bgcolor: "transparent",
            border: "none",
            outline: "none",
            padding: "10px",
            fontSize: "16px",
          }}
          maxLength={9}
        />
        {isDropdownOpen && (
          <Box
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              width: "100%",
              border: "1px solid #ccc",
              borderRadius: "4px",
              background: "white",
              boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
              zIndex: 10,
              overflow: "hidden",
            }}
          >
            {countryList.map((country) => (
              <Box
                key={country.code}
                onClick={() => {
                  setCountryCode(country.code);
                  setIsDropdownOpen(false);
                }}
                sx={{
                  padding: "5px 10px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <Image
                  src={country.flag}
                  alt={t`Flag of ${country.name}`}
                  width={20}
                  height={20}
                  style={{ marginRight: "8px" }}
                />
                <Typography sx={{ transition: ".3s ease", "&:hover": { color: "primary.main" } }}>
                  {country.name} (+{country.code})
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PhoneInputField;
